import { SellablePersistence } from "core/persistence";
import { ProductService } from "core/service";
import BaseRepository from "core/repository/base.repository";
import { Sellable } from "core/entity";
import { CategoryRepository, BarcodeRepository } from "core/repository";
import SaleRepository from "core/repository/sale.repository";
import Papa from "papaparse";
import Big from "big.js";

class ProductRepository extends BaseRepository {
  syncable = false;

  getEntityIdentifier() {
    return "sellable";
  }

  resourceName() {
    return "produtos";
  }

  getPersistenceProvider() {
    return SellablePersistence;
  }

  getServiceProvider() {
    return ProductService;
  }

  async modelToEntity(model) {
    const entity = new Sellable(model);

    const categoryResult = await CategoryRepository.get(entity.category_uuid);

    if (categoryResult.success) {
      entity.category = categoryResult.data;
    }

    return entity;
  }

  async getTotals({ filters }) {
    // TODO: Remove company_uuid from fetch
    const currentUser = await this.mRuntime.get("currentUser");
    const companyUuid = currentUser.company_uuid;

    filters.push({ field: "stock_count", operation: "GT", value: "0" });

    const result = await this.mService.fetch(companyUuid, {
      filters,
      group: {
        field: "company_uuid",
        transformations: [
          { field: "stock_count", operation: "SUM" },
          { field: "stock_value", operation: "SUM" }
        ]
      }
    });

    if (!result.success) {
      return { sucess: false };
    }

    if (result.data.length === 0) {
      return { success: true, data: { stock_count: 0, stock_value: 0 } };
    }

    return { success: true, data: result.data[0] };
  }

  async importFile(params) {
    return await this.mService.importFile(params);
  }

  async getImportStatus() {
    return await this.mService.getImportStatus();
  }

  async getExistingProduct(companyUuid, productUuid) {
    return this.mService.fetchByUuid(companyUuid, productUuid);
  }

  async updateProduct(companyUuid, productUuid, params) {
    return this.mService.updateProduct(companyUuid, productUuid, params);
  }

  async updateMany(companyUuid, products) {
    return this.mService.updateMany(companyUuid, products);
  }

  async deleteAll(companyUuid) {
    return this.mService.deleteAll(companyUuid);
  }

  async restoreAll(companyUuid) {
    return this.mService.restoreAll(companyUuid);
  }

  async delete(uuid) {
    const result = await super.update(uuid, { deleted: true });

    if (result.success) {
      this.mRuntime.fire(`sellable/delete`, uuid);
    }

    return result;
  }

  async restore(uuid) {
    return await super.update(uuid, { deleted: false });
  }

  async generateSkus(productUuid) {
    return await this.mService.generateSkus(productUuid);
  }

  async deleteSkus(productUuid) {
    return await this.mService.deleteSkus(productUuid);
  }

  async getSkuStock(productUuid) {
    const currentUser = await this.mRuntime.get("currentUser");
    const companyUuid = currentUser.company_uuid;

    const filters = [
      { field: "sku_uuid", operation: "EQ", value: productUuid }
      // { field: 'deleted', operation: 'NOT', value: true },
    ];

    const result = await this.mService.fetch(companyUuid, {
      filters,
      group: {
        field: "company_uuid",
        transformations: [{ field: "stock_count", operation: "SUM" }]
      }
    });

    if (!result.success) {
      return { sucess: false };
    }

    if (result.data.length === 0) {
      return { success: true, data: { stock_count: 0, stock_value: 0 } };
    }

    return { success: true, data: result.data[0] };
  }

  async getReportCsvContent({ order, filters, saleFilters }) {
    const listResult = await this.mPersistence.getList({
      order,
      filters,
      pagination: false
    });

    if (!listResult.success) {
      return listResult;
    }

    const header = [
      "Código",
      "Nome",
      "Quantidade Vendida",
      "Faturamento",
      "Custos",
      "Lucro Bruto"
    ];
    const data = await Promise.all(
      listResult.data.map(product =>
        this._productToReportCsvLine(product, saleFilters)
      )
    );
    const content = Papa.unparse({ fields: header, data: data });

    return { success: true, data: content };
  }

  async _productToReportCsvLine(model, saleFilters) {
    const product = await this.modelToEntity(model);
    const filters = await saleFilters(product);
    const repositoryResult = await SaleRepository.getReportSummary(
      filters,
      true
    );

    if (repositoryResult.success) {
      let absoluteProfit = "0.00";
      const profit = repositoryResult.data.item_count[model.uuid].profit;

      if (profit) {
        absoluteProfit = Big(profit)
          .div(100)
          .toFixed(2);
      }

      return [
        product.code,
        product.name,
        repositoryResult.data.item_count[model.uuid].count,
        Big(repositoryResult.data.item_count[model.uuid].revenues)
          .div(100)
          .toFixed(2),
        Big(repositoryResult.data.item_count[model.uuid].costs)
          .div(100)
          .toFixed(2),
        absoluteProfit
      ];
    } else {
      return [product.code, product.name, "-", "-", "-", "-"];
    }
  }

  async getInventoryCsv({ filters, order }) {
    const currentUser = await this.mRuntime.get("currentUser");
    const data = [];
    const success = await this.mService.stream(
      currentUser.company_uuid,
      { order, filters },
      async model => {
        data.push(await this._productToInventoryCsvLine(model));
      }
    );

    if (!success) {
      return { success };
    }

    const header = [
      "Código",
      "Nome",
      "Total Estoque",
      "Código(s) de Barras",
      "Contagem"
    ];
    const content = Papa.unparse({ fields: header, data: data });

    return { success: true, data: content };
  }

  async _productToInventoryCsvLine(product) {
    const barcodesResult = await BarcodeRepository.getAll({
      filters: [
        { field: "sellable_uuid", operation: "EQ", value: product.uuid }
      ]
    });

    let barcodes = "";

    if (barcodesResult.success) {
      barcodes = barcodesResult.data.map(barcode => barcode.code).join(", ");
    }

    return [product.code, product.name, product.stock_count, barcodes, ""];
  }

  async getAllBarcodes (barcodes, page) {
    const barcodesQueryString = barcodes.join().replace(/,/g, "|")

    const response = await BarcodeRepository.getListOnline({
      filters: [
        { field: "code", operation: "IN", value: barcodesQueryString },
        { field: "deleted", operation: "EQ", value: "false" }
      ],
      pagination: { page: page, perPage: 25 }
    });

    return response.success ? response.data : []
  }

  async getSellablesByUuid(uuidList) {
    const currentUser = await this.mRuntime.get("currentUser");
    const companyUuid = currentUser.company_uuid;

    const sellablesUuidQueryString = uuidList.join().replace(/,/g, "|")

    const filters = [
      { field: "uuid", operation: "IN", value: sellablesUuidQueryString },
      { field: "deleted", operation: "EQ", value: "false" }
    ]

    const response = await this.mService.fetch(companyUuid, { filters });

    return response.success ? response.data : []
  }

  async validateTaxes(params) {
    return await this.mService.validateTaxes(params);
  }
}

export default new ProductRepository();



// WEBPACK FOOTER //
// ./src/core/repository/product.repository.js